import React, { useState } from "react";
import { AppShell } from "../components/duvi/AppShell";
import { Card } from "../components/duvi/Card";
import { Button } from "../components/duvi/Button";
import { Badge } from "../components/duvi/Badge";
import { Select } from "../components/duvi/Select";
import { MultiSelect } from "../components/duvi/MultiSelect";
import { Download, Search, Settings, X } from "lucide-react";
import { audit, type AuditEvent } from "../api/client";
import { useQuery } from "../hooks/useApi";
import { testIds } from "@duvi-agency/ui";

export function AuditLog() {
  const [selectedActor, setSelectedActor] = useState("all");
  const [selectedActions, setSelectedActions] = useState<string[]>([]);
  const [selectedEntities, setSelectedEntities] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedEvent, setSelectedEvent] = useState<AuditEvent | null>(null);

  const { data: eventsData, isLoading } = useQuery(
    () =>
      audit.list({
        actor: selectedActor !== "all" ? selectedActor : undefined,
        action: selectedActions.join(",") || undefined,
        entity_type: selectedEntities.join(",") || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      }),
    [selectedActor, selectedActions, selectedEntities, dateFrom, dateTo],
  );

  const events = (eventsData ?? []).map((e) => {
    const actorName = e.actor_name || e.actor_id || "System";
    const isSystem = !e.actor_id && !e.actor_name;
    return {
      id: e.event_id,
      timestamp: e.timestamp,
      actor: {
        type: isSystem ? "system" : "user",
        name: actorName,
        avatar: isSystem
          ? null
          : actorName
              .split(" ")
              .map((p: string) => p[0])
              .join("")
              .toUpperCase()
              .slice(0, 2),
      },
      action: e.action ?? "",
      entity: {
        type: e.entity_type ?? "",
        name: e.entity_id ?? "",
      },
      detail: e.detail ?? "",
      ip: e.ip_address ?? "",
      userAgent: e.user_agent ?? "",
      before: e.before ?? null,
      after: e.after ?? null,
      _raw: e,
    };
  });

  // Build actors list from loaded data
  const actorSet = new Map<
    string,
    { id: string; name: string; avatar: string | null }
  >();
  actorSet.set("all", { id: "all", name: "All users", avatar: null });
  for (const e of eventsData ?? []) {
    const aId = e.actor_id || "system";
    if (!actorSet.has(aId)) {
      const aName = e.actor_name || e.actor_id || "System";
      actorSet.set(aId, {
        id: aId,
        name: aName,
        avatar:
          aName === "System"
            ? null
            : aName
                .split(" ")
                .map((p: string) => p[0])
                .join("")
                .toUpperCase()
                .slice(0, 2),
      });
    }
  }
  const actors = Array.from(actorSet.values());

  const actionTypes = [
    "created",
    "updated",
    "deleted",
    "invited",
    "disabled",
    "resolved",
    "pushed",
  ];
  const entityTypes = ["ticket", "article", "user"];

  const getActionColor = (action: string) => {
    switch (action) {
      case "created":
        return "success";
      case "updated":
        return "warning";
      case "deleted":
        return "destructive";
      case "invited":
        return "info";
      case "disabled":
        return "destructive";
      case "resolved":
        return "success";
      case "pushed":
        return "info";
      default:
        return "default";
    }
  };

  const handleExportCsv = () => {
    if (!events.length) return;

    const headers = [
      "Timestamp",
      "Actor",
      "Action",
      "Entity Type",
      "Entity Name",
      "Detail",
      "IP Address",
    ];
    const rows = events.map((e) => [
      e.timestamp,
      e.actor.name,
      e.action,
      e.entity.type,
      e.entity.name,
      e.detail,
      e.ip,
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const EmptyState = () => (
    <div
      data-testid={testIds.auditEmpty}
      className="flex flex-col items-center justify-center py-16"
    >
      <Search
        className="w-16 h-16 text-duvi-text-secondary mb-6"
        strokeWidth={1}
      />
      <h2
        className="text-duvi-text-primary mb-2"
        style={{
          fontSize: "var(--text-lg)",
          fontWeight: "var(--font-weight-semibold)",
        }}
      >
        No events match your filters
      </h2>
    </div>
  );

  return (
    <AppShell
      breadcrumbs={["Platform", "Audit Log"]}
      activeMenuItem="Audit Log"
    >
      <div data-testid={testIds.auditLogPage} className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1
              className="text-duvi-text-primary mb-2"
              style={{
                fontSize: "var(--text-xl)",
                fontWeight: "var(--font-weight-semibold)",
                lineHeight: "var(--line-height-heading)",
              }}
            >
              Audit Log
            </h1>
            <p
              className="text-duvi-text-secondary"
              style={{ fontSize: "var(--text-sm)" }}
            >
              All platform admin actions
            </p>
          </div>
          <Button
            variant="secondary"
            onClick={handleExportCsv}
            data-testid={testIds.auditExportCsv}
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="grid grid-cols-4 gap-4">
            {/* Actor Filter */}
            <div>
              <label
                className="block text-duvi-text-primary mb-2"
                style={{
                  fontSize: "var(--text-xs)",
                  fontWeight: "var(--font-weight-medium)",
                }}
              >
                Actor
              </label>
              <Select
                value={selectedActor}
                onChange={(e) => setSelectedActor(e.target.value)}
                data-testid={testIds.auditFilterActor}
              >
                {actors.map((actor) => (
                  <option key={actor.id} value={actor.id}>
                    {actor.name}
                  </option>
                ))}
              </Select>
            </div>

            {/* Action Type Filter */}
            <div data-testid={testIds.auditFilterAction}>
              <label
                className="block text-duvi-text-primary mb-2"
                style={{
                  fontSize: "var(--text-xs)",
                  fontWeight: "var(--font-weight-medium)",
                }}
              >
                Action Type
              </label>
              <MultiSelect
                options={actionTypes.map((a) => ({
                  value: a,
                  label: a.charAt(0).toUpperCase() + a.slice(1),
                }))}
                value={selectedActions}
                onChange={setSelectedActions}
                placeholder="All actions"
                searchPlaceholder="Search actions..."
              />
            </div>

            {/* Entity Type Filter */}
            <div data-testid={testIds.auditFilterEntity}>
              <label
                className="block text-duvi-text-primary mb-2"
                style={{
                  fontSize: "var(--text-xs)",
                  fontWeight: "var(--font-weight-medium)",
                }}
              >
                Entity Type
              </label>
              <MultiSelect
                options={entityTypes.map((e) => ({
                  value: e,
                  label: e.charAt(0).toUpperCase() + e.slice(1),
                }))}
                value={selectedEntities}
                onChange={setSelectedEntities}
                placeholder="All entities"
                searchPlaceholder="Search entities..."
              />
            </div>

            {/* Date Range Filter */}
            <div>
              <label
                className="block text-duvi-text-primary mb-2"
                style={{
                  fontSize: "var(--text-xs)",
                  fontWeight: "var(--font-weight-medium)",
                }}
              >
                Date Range
              </label>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  data-testid={testIds.auditFilterDateFrom}
                  className="flex-1 px-3 py-2 bg-duvi-bg border border-duvi-border rounded-[6px] text-duvi-text-primary focus:outline-none focus:border-duvi-accent transition-colors"
                  style={{ fontSize: "var(--text-sm)" }}
                  placeholder="From"
                />
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  data-testid={testIds.auditFilterDateTo}
                  className="flex-1 px-3 py-2 bg-duvi-bg border border-duvi-border rounded-[6px] text-duvi-text-primary focus:outline-none focus:border-duvi-accent transition-colors"
                  style={{ fontSize: "var(--text-sm)" }}
                  placeholder="To"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Events Table */}
        {isLoading ? (
          <Card className="p-6">
            <div className="flex items-center justify-center py-16">
              <div
                className="text-duvi-text-secondary"
                style={{ fontSize: "var(--text-sm)" }}
              >
                Loading audit events...
              </div>
            </div>
          </Card>
        ) : events.length === 0 ? (
          <Card className="p-6">
            <EmptyState />
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-duvi-border">
                  <tr>
                    <th
                      className="text-left py-3 px-4 text-duvi-text-secondary"
                      style={{
                        fontSize: "var(--text-xs)",
                        fontWeight: "var(--font-weight-medium)",
                      }}
                    >
                      Timestamp
                    </th>
                    <th
                      className="text-left py-3 px-4 text-duvi-text-secondary"
                      style={{
                        fontSize: "var(--text-xs)",
                        fontWeight: "var(--font-weight-medium)",
                      }}
                    >
                      Actor
                    </th>
                    <th
                      className="text-left py-3 px-4 text-duvi-text-secondary"
                      style={{
                        fontSize: "var(--text-xs)",
                        fontWeight: "var(--font-weight-medium)",
                      }}
                    >
                      Action
                    </th>
                    <th
                      className="text-left py-3 px-4 text-duvi-text-secondary"
                      style={{
                        fontSize: "var(--text-xs)",
                        fontWeight: "var(--font-weight-medium)",
                      }}
                    >
                      Entity
                    </th>
                    <th
                      className="text-left py-3 px-4 text-duvi-text-secondary"
                      style={{
                        fontSize: "var(--text-xs)",
                        fontWeight: "var(--font-weight-medium)",
                      }}
                    >
                      Detail
                    </th>
                    <th
                      className="text-left py-3 px-4 text-duvi-text-secondary"
                      style={{
                        fontSize: "var(--text-xs)",
                        fontWeight: "var(--font-weight-medium)",
                      }}
                    >
                      IP Address
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <tr
                      key={event.id}
                      data-testid={testIds.auditRow}
                      onClick={() => setSelectedEvent(event._raw)}
                      className="border-b border-duvi-border hover:bg-duvi-elevated transition-colors cursor-pointer"
                    >
                      <td
                        className="py-3 px-4 text-duvi-text-secondary font-mono"
                        style={{ fontSize: "var(--text-xs)" }}
                      >
                        {event.timestamp}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {event.actor.type === "system" ? (
                            <Settings className="w-4 h-4 text-duvi-text-secondary" />
                          ) : (
                            <div
                              className="w-4 h-4 rounded-full bg-duvi-accent flex items-center justify-center text-white flex-shrink-0"
                              style={{
                                fontSize: "8px",
                                fontWeight: "var(--font-weight-semibold)",
                              }}
                            >
                              {event.actor.avatar}
                            </div>
                          )}
                          <span
                            className="text-duvi-text-primary"
                            style={{ fontSize: "var(--text-xs)" }}
                          >
                            {event.actor.name}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant={getActionColor(event.action)}>
                          {event.action}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className="inline-block px-2 py-1 bg-duvi-surface rounded text-duvi-text-primary"
                          style={{ fontSize: "var(--text-xs)" }}
                        >
                          {event.entity.type}
                        </span>
                        <span
                          className="ml-2 text-duvi-text-secondary"
                          style={{ fontSize: "var(--text-xs)" }}
                        >
                          {event.entity.name}
                        </span>
                      </td>
                      <td
                        className="py-3 px-4 text-duvi-text-secondary truncate max-w-xs"
                        style={{ fontSize: "var(--text-xs)" }}
                      >
                        {event.detail}
                      </td>
                      <td
                        className="py-3 px-4 text-duvi-text-secondary font-mono"
                        style={{ fontSize: "var(--text-xs)" }}
                      >
                        {event.ip}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-duvi-border">
              <div
                className="text-duvi-text-secondary"
                style={{ fontSize: "var(--text-xs)" }}
              >
                Showing {events.length} of {events.length} events
              </div>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" disabled>
                  Previous
                </Button>
                <Button variant="secondary" size="sm" disabled>
                  Next
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Event Detail Slide-over */}
      {selectedEvent && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setSelectedEvent(null)}
          />
          <div
            data-testid={testIds.auditDetailPanel}
            className="fixed top-0 right-0 h-full bg-duvi-elevated border-l border-duvi-border shadow-2xl z-50 flex flex-col overflow-hidden"
            style={{ width: "600px" }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-duvi-border">
              <h2
                className="text-duvi-text-primary"
                style={{
                  fontSize: "var(--text-lg)",
                  fontWeight: "var(--font-weight-bold)",
                }}
              >
                Event Details
              </h2>
              <button
                onClick={() => setSelectedEvent(null)}
                className="p-1 hover:bg-duvi-surface rounded transition-colors text-duvi-text-secondary"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
              {/* Metadata */}
              <div>
                <h3
                  className="text-duvi-text-primary mb-3"
                  style={{
                    fontSize: "var(--text-sm)",
                    fontWeight: "var(--font-weight-semibold)",
                  }}
                >
                  Metadata
                </h3>
                <div
                  className="space-y-2 text-duvi-text-secondary"
                  style={{ fontSize: "var(--text-sm)" }}
                >
                  <div className="flex justify-between">
                    <span className="text-duvi-text-secondary">Timestamp:</span>
                    <span className="font-mono">{selectedEvent.timestamp}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-duvi-text-secondary">Actor:</span>
                    <span>{selectedEvent.actor_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-duvi-text-secondary">
                      IP Address:
                    </span>
                    <span className="font-mono">
                      {selectedEvent.ip_address ?? ""}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-duvi-text-secondary">
                      User Agent:
                    </span>
                    <span className="text-right ml-4 break-all">
                      {selectedEvent.user_agent ?? ""}
                    </span>
                  </div>
                </div>
              </div>

              {/* Before/After Diff */}
              <div>
                <h3
                  className="text-duvi-text-primary mb-3"
                  style={{
                    fontSize: "var(--text-sm)",
                    fontWeight: "var(--font-weight-semibold)",
                  }}
                >
                  Changes
                </h3>

                {selectedEvent.before && (
                  <div className="mb-4">
                    <div
                      className="text-duvi-text-secondary mb-2"
                      style={{ fontSize: "var(--text-xs)" }}
                    >
                      Before:
                    </div>
                    <pre className="bg-duvi-bg border border-duvi-border rounded-[6px] p-4 overflow-x-auto">
                      <code
                        className="text-duvi-destructive"
                        style={{ fontSize: "var(--text-xs)" }}
                      >
                        {JSON.stringify(selectedEvent.before, null, 2)}
                      </code>
                    </pre>
                  </div>
                )}

                {selectedEvent.after && (
                  <div>
                    <div
                      className="text-duvi-text-secondary mb-2"
                      style={{ fontSize: "var(--text-xs)" }}
                    >
                      After:
                    </div>
                    <pre className="bg-duvi-bg border border-duvi-border rounded-[6px] p-4 overflow-x-auto">
                      <code
                        className="text-duvi-success"
                        style={{ fontSize: "var(--text-xs)" }}
                      >
                        {JSON.stringify(selectedEvent.after, null, 2)}
                      </code>
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </AppShell>
  );
}
